import { useEffect, useRef, useState } from 'react';
import { useEditor } from '../state/store';
import type { SectionGroup, SectionMetadata } from '../api/types';
import { renderSection } from '../api/client';
import { sectionSettingRef, blockSettingRef } from '../lib/markers';
import {
  patchSettingInDom,
  patchCssVar,
  patchCssVarRoot,
  replaceSectionHtml,
  insertSectionHtml,
  removeSectionFromDom,
  moveSectionInDom,
} from '../preview/dom-patcher';
import {
  sectionCssVarsFor,
  formatValueForSectionCssVar,
} from '../preview/css-var-patcher';
import {
  getHandle,
  installOverlay,
  uninstallOverlay,
  installClickHandler,
  installNavigationInterceptor,
  applySelection,
} from '../preview/iframe-bridge';
import {
  installMobileEmulator,
  type MobileEmulatorHandle,
} from '../preview/mobile-emulator';
import {
  cssVarPatchFromSettings,
  applyCssVarPatch,
  knownCssVarSettings,
} from '../preview/css-var-patcher';

function findSection(
  state: ReturnType<typeof useEditor.getState>,
  sectionId: string
): { group: SectionGroup; section: SectionMetadata } | null {
  if (state.sections[sectionId]) return { group: 'template', section: state.sections[sectionId] };
  if (state.headerGroup?.sections[sectionId])
    return { group: 'header-group', section: state.headerGroup.sections[sectionId] };
  if (state.footerGroup?.sections[sectionId])
    return { group: 'footer-group', section: state.footerGroup.sections[sectionId] };
  if (state.cartDrawerGroup?.sections[sectionId])
    return { group: 'cart-drawer-group', section: state.cartDrawerGroup.sections[sectionId] };
  return null;
}

const RICH_TYPES = new Set(['richtext', 'html', 'textarea']);
function isRichText(type: string | undefined): boolean {
  return !!type && RICH_TYPES.has(type.toLowerCase());
}

export function Preview() {
  const iframeRef = useRef<HTMLIFrameElement>(null);
  const [ready, setReady] = useState(false);

  const initialHtml = useEditor((s) => s.initialHtml);
  const device = useEditor((s) => s.device);
  const selection = useEditor((s) => s.selection);
  const select = useEditor((s) => s.select);
  const loading = useEditor((s) => s.loading);
  const previewEditable = useEditor((s) => s.previewEditable);
  const page = useEditor((s) => s.page);
  const cartPreviewMode = useEditor((s) => s.cartPreviewMode);

  // Scroll preservation across iframe reloads.
  //
  // Quando o usuário altera uma config que força layout reload
  // (`scheduleLayoutReload` → `reloadPage()`), o store recebe um novo
  // `initialHtml` e o useEffect abaixo faz `iframe.srcdoc = html`. Isso
  // desmonta o document inteiro e o scroll volta pra 0.
  //
  // Estratégia:
  //   1. Listener contínuo `scroll` no iframe salva a posição em tempo
  //      real num ref. Evita a race condition de só capturar no momento
  //      do `srcdoc` change (quando o document já pode estar em
  //      transição).
  //   2. No onLoad do novo doc, restaura via múltiplas tentativas com
  //      atraso crescente — `requestAnimationFrame` sozinho não basta
  //      quando o body ainda tá crescendo (imagens carregando, etc.).
  //   3. Só restaura se a PÁGINA (index, product, etc.) não mudou —
  //      navegação começa do topo.
  const savedScrollRef = useRef<{ page: string; y: number } | null>(null);
  const lastRenderedPageRef = useRef<string>('');

  useEffect(() => {
    if (!initialHtml || !iframeRef.current) return;
    const iframe = iframeRef.current;

    // Snapshot do scroll ATUAL (antes de trocar srcdoc). Se o listener
    // contínuo já populou o ref nessa sessão, isso valida que ainda
    // tá na mesma página antes de usar.
    try {
      const doc = iframe.contentDocument;
      const win = iframe.contentWindow;
      const y = win?.scrollY ?? doc?.documentElement?.scrollTop ?? 0;
      if (y > 0 && lastRenderedPageRef.current === page) {
        savedScrollRef.current = { page, y };
      }
      // Se já temos um scroll salvo do listener e bate com a página,
      // mantém. Só zera em navegação de página.
      if (lastRenderedPageRef.current && lastRenderedPageRef.current !== page) {
        savedScrollRef.current = null;
      }
    } catch {
      /* cross-origin ou sandbox — ignora */
    }

    setReady(false);
    iframe.removeAttribute('src');
    iframe.srcdoc = initialHtml;

    let scrollListenerCleanup: (() => void) | null = null;

    const onLoad = () => {
      setReady(true);
      lastRenderedPageRef.current = page;

      // Instala listener no novo documento pra capturar scroll em tempo
      // real — assim a próxima mudança de config já tem o valor
      // atualizado sem depender de ler scrollY no momento exato.
      try {
        const win = iframe.contentWindow;
        if (win) {
          const onScroll = () => {
            try {
              const y = win.scrollY;
              savedScrollRef.current = { page, y };
            } catch {}
          };
          win.addEventListener('scroll', onScroll, { passive: true });
          scrollListenerCleanup = () => {
            try { win.removeEventListener('scroll', onScroll); } catch {}
          };
        }
      } catch {}

      // Restore scroll on same-page reloads — múltiplas tentativas
      // pra vencer o layout shift (imagens, fonts, lazy content) que
      // pode clamp o scrollTo pra 0 nos primeiros frames.
      const saved = savedScrollRef.current;
      if (!saved || saved.page !== page || saved.y <= 0) return;
      const targetY = saved.y;
      const tryRestore = (attempts: number) => {
        try {
          const win = iframe.contentWindow;
          if (!win) return;
          const max =
            (win.document.documentElement.scrollHeight || 0) - (win.innerHeight || 0);
          const clampedTarget = Math.min(targetY, Math.max(0, max));
          win.scrollTo(0, clampedTarget);
          if (attempts > 0 && Math.abs(win.scrollY - clampedTarget) > 2) {
            setTimeout(() => tryRestore(attempts - 1), 50);
          }
        } catch {}
      };
      // Tenta 6 vezes em intervalos crescentes — cobre layout shift
      // normal + carregamento de imagens lazy do produto.
      setTimeout(() => tryRestore(6), 0);

      // Re-dispara o "trigger" do carrinho se o usuário estava em
      // modo gaveta/mensagem antes do reload. Sem isso, mexer numa
      // config global com o drawer aberto fecharia o drawer (o iframe
      // recarrega sem memória do estado de UI).
      //
      // GATE TRIPLO (set 2026): só re-dispara quando TODOS forem true:
      //   1) `cartPreviewMode` em 'drawer' ou 'message'
      //   2) `state.page === 'cart'` (React state)
      //   3) URL do iframe é /cart (DOM real)
      //
      // O #3 cobre o caso onde o usuário clica num link DENTRO do
      // iframe (logo, menu de navegação) — o iframe navega via
      // browser sem passar pelo Toolbar/setPage, então `state.page`
      // não atualiza e fica preso em 'cart'. A reload da nova página
      // (home, produto, etc.) chamava onLoad → state.page ainda
      // 'cart' → dispatch acontecia → drawer abria em cima da
      // página errada. Bug reportado: "PORQUE O CARRINHO TA ABRINDO
      // SOZINHO SENDO QUE TO NA HOME PORRA?"
      //
      // Agora se o iframe acabou em /, /products/X ou /collections/X,
      // a URL do iframe não é /cart, gate falha, drawer fica fechado.
      // Também sincronizamos `state.page` pra 'index' nesse caso pra
      // o próximo onLoad e pra UI geral.
      const state = useEditor.getState();
      const mode = state.cartPreviewMode;
      const currentPage = state.page;
      let iframeUrl = '';
      let editorPage = '';
      try {
        iframeUrl = (iframe.contentWindow?.location?.pathname || '').toLowerCase();
        editorPage =
          ((iframe.contentWindow as any)?.Shopify?.editorPage || '').toLowerCase();
      } catch {}
      /*
        No editor srcdoc, `pathname` fica `/` ou `about:srcdoc`. O engine
        injeta `window.Shopify.editorPage` (o "logical page" que o
        editor pediu pra renderizar — `cart`, `index`, etc.) pra dar
        uma fonte de verdade independente do URL real do iframe.

        Quando o user clica num link DENTRO do iframe e navega de fato
        pra outra página, o iframe sai do srcdoc e `pathname` vira
        `/products/X` (ou outra URL real). Nesse caso `editorPage`
        fica stale com o valor antigo, então caímos no path-based check.
      */
      const isPathCart = iframeUrl === '/cart' || iframeUrl === '/cart/' || iframeUrl === '/carrinho' || iframeUrl === '/carrinho/';
      const isSrcdocLike = !iframeUrl || iframeUrl === '/' || iframeUrl === '/srcdoc' || iframeUrl === '';
      const iframeOnCart = isPathCart || (isSrcdocLike && editorPage === 'cart');

      // Se a URL do iframe é REAL e NÃO é /cart mas o React acha que é,
      // sincroniza: limpa cartPreviewMode pra evitar loops e ajusta
      // page (best-effort, não conhece todas as rotas). Não fazemos isso
      // pra srcdoc porque srcdoc-load É o load inicial — `editorPage`
      // já cobriu a checagem.
      if (currentPage === 'cart' && !iframeOnCart && !isSrcdocLike && iframeUrl) {
        try { useEditor.getState().setCartPreviewMode(null); } catch {}
      }

      if (
        (mode === 'drawer' || mode === 'message') &&
        currentPage === 'cart' &&
        iframeOnCart
      ) {
        try {
          const win = iframe.contentWindow;
          if (win) {
            // Pequeno delay pra o listener do drawer-cart (que só
            // registra quando o <script> do snippet executa) já
            // estar instalado antes do dispatch.
            setTimeout(() => {
              try {
                win.dispatchEvent(
                  new CustomEvent('theme:cart:open', {
                    detail: { source: 'editor', reopen: true },
                  })
                );
              } catch {}
            }, 150);
          }
        } catch {}
      }
    };
    iframe.addEventListener('load', onLoad);
    return () => {
      iframe.removeEventListener('load', onLoad);
      scrollListenerCleanup?.();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [initialHtml, page]);

  //
  // Dispatch `theme:cart:open` quando user troca pra modo drawer/message.
  // O onLoad acima cobre o caso de RELOAD do iframe, mas não o caso onde
  // o iframe já tá em /cart e o user só MUDA o cartPreviewMode (ex: clica
  // "Carrinho (gaveta)" estando já em outra page → setPage('cart') aciona
  // reload, dispatcher do onLoad funciona; MAS estando já em /cart ou
  // mudando entre drawer/message, onLoad não dispara).
  //
  useEffect(() => {
    if (!ready || !iframeRef.current) return;
    if (cartPreviewMode !== 'drawer' && cartPreviewMode !== 'message') return;
    if (page !== 'cart') return;
    const win = iframeRef.current.contentWindow;
    if (!win) return;
    let iframeUrl = '';
    let editorPage = '';
    try {
      iframeUrl = (win.location?.pathname || '').toLowerCase();
      editorPage = ((win as any).Shopify?.editorPage || '').toLowerCase();
    } catch {}
    const isPathCart = iframeUrl === '/cart' || iframeUrl === '/cart/' || iframeUrl === '/carrinho' || iframeUrl === '/carrinho/';
    const isSrcdocLike = !iframeUrl || iframeUrl === '/' || iframeUrl === '/srcdoc' || iframeUrl === '';
    const iframeOnCart = isPathCart || (isSrcdocLike && editorPage === 'cart');
    if (!iframeOnCart) return;
    const t = setTimeout(() => {
      try {
        win.dispatchEvent(
          new CustomEvent('theme:cart:open', {
            detail: { source: 'editor', reopen: true },
          })
        );
      } catch {}
    }, 150);
    return () => clearTimeout(t);
  }, [cartPreviewMode, page, ready]);

  // Install/uninstall the editor overlay + click-to-select handler based
  // on `previewEditable`. Runs whenever the iframe becomes ready OR the
  // toggle flips, so the user can turn chrome on/off without reloading.
  useEffect(() => {
    if (!ready || !iframeRef.current) return;
    const h = getHandle(iframeRef.current);
    if (!h) return;

    /*
      Roteador de intent. Compartilhado entre dois canais:
        1) installNavigationInterceptor — escuta clicks na window do
           PARENT e roteia. Funciona quando o parent observa o iframe.
        2) postMessage `luna:nav` — listener inserido pelo engine no
           próprio iframe (context-builder.ts). Mais robusto contra
           race conditions de timing porque o script roda antes de
           qualquer click possível.

      Os dois caminhos podem disparar pra o mesmo click (overlap). Não
      tem problema — setPage idempotente, dispatchEvent extra é no-op.
    */
    const routeIntent = (intent: import('../preview/iframe-bridge').NavigationIntent) => {
      const s = useEditor.getState();
      switch (intent.kind) {
        case 'home':
          void s.setPage('index');
          break;
        case 'product':
          void s.setPreviewProduct(intent.slug);
          void s.setPage('product');
          break;
        case 'collection':
          void s.setPreviewCollection(intent.id);
          void s.setPage('collection');
          break;
        case 'page':
          // Páginas CMS são templates próprios no lc-store; tenta
          // navegar pra `page-${handle}`. Se não existir, o setPage
          // falha silenciosamente e o usuário fica onde tá.
          void s.setPage(`page-${intent.handle}`);
          break;
        case 'cart': {
          /*
            Lojista clicou no ícone do carrinho (header) ou em qualquer
            `<a href="/cart">`. Em modo drawer/message o esperado é
            ABRIR a gaveta — não navegar pra outra página. O preventDefault
            no interceptor bloqueia a navegação real; aqui despachamos
            `theme:cart:open` direto no iframe pra reusar o mesmo trigger
            que o ícone faria no storefront real.

            Em modo `page` (cart_type === 'page') ainda navegamos pro
            template `cart` — comportamento legado pra lojas que querem
            carrinho como página standalone.
          */
          const cartType = (s.globalSettings?.cart_type as string) || 'page';
          if (cartType === 'drawer' || cartType === 'message') {
            // Garante que o cartPreviewMode está sincronizado (ex.: lojista
            // tava navegando outra page e clicou na sacola).
            s.setCartPreviewMode(cartType === 'message' ? 'message' : 'drawer');
            const win = h.win;
            try {
              win.dispatchEvent(
                new CustomEvent('theme:cart:open', {
                  detail: { source: 'editor', via: 'cart-icon' },
                })
              );
            } catch (err) {
              console.warn('[preview] cart open dispatch failed', err);
            }
          } else {
            // Modo page — vai pra rota /cart como página
            void s.setPage('cart');
          }
          break;
        }
        case 'external':
          // Já abrimos em nova aba dentro do interceptor.
          break;
        case 'unknown':
          console.warn('[preview] navegação não roteada:', intent.href);
          break;
      }
    };

    // Canal 1: parent-side click interceptor.
    const navTeardown = installNavigationInterceptor(h, routeIntent);

    // Canal 2: postMessage `luna:nav` do iframe (engine injeta o
    // script in-iframe no <head> via context-builder).
    const messageHandler = (ev: MessageEvent) => {
      // Só aceita messages do contentWindow do iframe atual.
      if (ev.source !== h.win) return;
      const data = ev.data;
      if (!data || typeof data !== 'object' || data.type !== 'luna:nav') return;
      const intent = data.intent;
      if (intent && typeof intent === 'object' && typeof intent.kind === 'string') {
        routeIntent(intent as any);
      }
    };
    window.addEventListener('message', messageHandler);

    if (!previewEditable) {
      uninstallOverlay(h);
      // Navigation interceptor + message listener ativos; editor chrome off.
      return () => {
        navTeardown?.();
        window.removeEventListener('message', messageHandler);
      };
    }

    installOverlay(h);
    const teardown = installClickHandler(h, (payload) => {
      if (payload.kind === 'block') {
        select({
          kind: 'block',
          group: payload.group,
          sectionId: payload.sectionId,
          blockId: payload.blockId,
        });
      } else {
        select({ kind: 'section', group: payload.group, sectionId: payload.sectionId });
      }
    });
    return () => {
      teardown?.();
      navTeardown?.();
      window.removeEventListener('message', messageHandler);
      uninstallOverlay(h);
    };
  }, [ready, previewEditable, select]);

  useEffect(() => {
    if (!ready || !iframeRef.current) return;
    if (!previewEditable) return; // selection outline lives in overlay CSS
    const h = getHandle(iframeRef.current);
    if (h) applySelection(h, selection);
  }, [ready, selection, previewEditable]);

  // Mobile emulation — synthesises TouchEvents from mouse drags and
  // flags the iframe's navigator as touch-capable. When the user
  // flips the device back to desktop we tear it down so the real
  // mouse experience is restored without an iframe reload.
  useEffect(() => {
    if (!ready || !iframeRef.current) return;
    if (device !== 'mobile') return;
    const h = getHandle(iframeRef.current);
    if (!h) return;
    let emu: MobileEmulatorHandle | null = null;
    try {
      emu = installMobileEmulator(h);
    } catch (err) {
      // Never let emulator wiring break the preview. Log and carry on.
      // eslint-disable-next-line no-console
      console.warn('[preview] mobile emulator failed to install', err);
    }
    return () => {
      emu?.uninstall();
    };
  }, [ready, device]);

  useEffect(() => {
    if (!ready || !iframeRef.current) return;
    const iframe = iframeRef.current;
    let prevSections = snapshotSections(useEditor.getState());
    let prevGlobals = JSON.stringify(useEditor.getState().globalSettings);

    const unsub = useEditor.subscribe((s) => {
      const h = getHandle(iframe);
      if (!h) return;

      // Global theme settings pipeline:
      //
      //   1. FAST path — CSS var patch. For settings that map to a
      //      CSS custom property (colors, font sizes, etc.) we
      //      update `:root` directly in the iframe. `var(--foo)`
      //      consumers in the rendered HTML recompute in the next
      //      frame — no network, no flicker, no DOM swap. This
      //      covers ~95% of color/size drags.
      //   2. REFLECT path — for the remaining settings (those still
      //      baked into section <style> at render time) we ask
      //      every section to re-render with the new globals so the
      //      inline Liquid `{{ current.foo }}` produces the new
      //      value. Debounced 120 ms to coalesce drag bursts.
      //   3. LAYOUT path — some settings only take effect after
      //      layout/theme.liquid re-renders (sticky WhatsApp
      //      toggle, favicon, body font, etc.). Debounced 600 ms
      //      full page reload; fired after the user stops
      //      interacting.
      const nextGlobals = JSON.stringify(s.globalSettings);
      if (nextGlobals !== prevGlobals) {
        // Compute which keys actually changed. Lets us scope the
        // fast-path patch to just the drifted values and decide
        // whether a full section re-render is even needed.
        const prev = JSON.parse(prevGlobals || '{}') as Record<string, any>;
        const changed: Record<string, any> = {};
        for (const k of Object.keys(s.globalSettings)) {
          if (s.globalSettings[k] !== prev[k]) changed[k] = s.globalSettings[k];
        }
        for (const k of Object.keys(prev)) {
          if (!(k in s.globalSettings)) changed[k] = undefined;
        }
        prevGlobals = nextGlobals;

        // 1. FAST path — patch CSS vars right now.
        const patch = cssVarPatchFromSettings(changed);
        if (patch.length > 0) {
          const applied = applyCssVarPatch(h.doc, patch);
          console.log(
            `[live-patch:global] ${Object.keys(changed).join(',')} →`,
            patch.map((p) => `${p.name}=${p.value}`).join(' '),
            `(applied to root: ${applied.length})`
          );
        } else {
          // Diagnóstico: setting global mudou mas não tem CSS var
          // mapeada. Ou é setting que precisa re-render de section
          // (legitimate), ou é bug de discoverer (var declarada no
          // css-variables.liquid mas não detectada).
          console.log(
            `[live-patch:global] ${Object.keys(changed).join(',')} → no CSS var mapping (will need full re-render if not covered by section vars)`
          );
        }

        // 2. REFLECT + LAYOUT paths share the same gate: only
        //    trigger when AT LEAST ONE changed setting isn't covered
        //    by a CSS var. If the user only dragged colors/sizes
        //    that live in var(--…) form (which is the overwhelming
        //    majority after the var migration), skip both paths
        //    entirely — the fast-path patch already produced the
        //    right visual and any rerender would just produce a
        //    flicker.
        //
        //    Non-CSS-var settings DO need the full path: toggles
        //    that gate DOM nodes (`show_sticky_whats`, favicon,
        //    body font, section-level settings not yet migrated),
        //    anything a `{% if current.foo %}` in layout.liquid
        //    consumes.
        const cssVarKeys = knownCssVarSettings();
        const needsFullPath = Object.keys(changed).some(
          (k) => !cssVarKeys.has(k)
        );

        /*
          FORCED layout reload: settings que MESMO tendo CSS var
          binding (live-patch OK) precisam de re-render do layout
          porque mexem em <link>/<script>/markup dinâmico do
          `theme.liquid`.

          `heading_font` / `text_font`: o `<link>` do Google Fonts
          em `theme.liquid` é montado dinâmicamente baseado nas
          settings. Live-patch troca a CSS var instantâneo, mas a
          fonte NOVA ainda não foi carregada — sem o link tag novo,
          o browser cai no fallback sans-serif. Forçar layout reload
          re-emite o <link> e o Google Fonts entrega o arquivo.
        */
        const FORCE_LAYOUT_RELOAD_SETTINGS = new Set([
          'heading_font',
          'text_font',
        ]);
        const fontChanged = Object.keys(changed).some((k) =>
          FORCE_LAYOUT_RELOAD_SETTINGS.has(k)
        );

        if ((needsFullPath || fontChanged) && s.session) {
          for (const grp of ['template', 'header-group', 'footer-group', 'cart-drawer-group'] as const) {
            for (const sid of orderForGroup(s, grp)) {
              scheduleRerender(h.doc, s.session, sid, grp);
            }
          }
          scheduleLayoutReload();
        }
      }

      const next = snapshotSections(s);
      const diff = diffSections(prevSections, next);
      prevSections = next;
      if (diff.length === 0) return;
      for (const d of diff) applyDiff(h.doc, d, s);
    });

    return () => unsub();
  }, [ready]);

  return (
    <div className="relative flex-1 overflow-hidden bg-surface-subdued">
      {loading && (
        <div className="absolute inset-0 z-10 flex items-center justify-center bg-surface-subdued/70 backdrop-blur-sm">
          <div className="flex items-center gap-2 rounded-full bg-surface px-4 py-2 text-[13px] text-text shadow-card">
            <div className="lc-spin h-4 w-4 rounded-full border-2 border-border border-t-interactive" />
            Carregando preview…
          </div>
        </div>
      )}

      {/*
        One single JSX tree for both devices. The iframe's DOM parent
        NEVER changes — the wrappers just toggle classes. Moving an
        iframe in the DOM would force the browser to re-navigate, which
        dropped the srcdoc and caused ERR_FILE_NOT_FOUND.
      */}
      <div
        className={
          'h-full w-full ' +
          (device === 'mobile'
            ? 'flex items-start justify-center overflow-auto p-8'
            : 'p-3')
        }
      >
        <div
          className={
            'relative ' +
            (device === 'mobile'
              ? 'shrink-0 rounded-[38px] bg-[#1a1a1a] p-3 shadow-[0_25px_50px_-12px_rgba(0,0,0,0.25)]'
              : 'h-full w-full overflow-hidden rounded-lg border border-border bg-white shadow-card')
          }
          style={device === 'mobile' ? { width: 390 + 24 } : undefined}
        >
          {/* Notch decoration is always rendered; device controls its
              visibility. Keeping it in the tree unconditionally means the
              iframe's sibling position doesn't shift, so React never
              re-inserts the iframe (which would trigger a browser reload
              and lose the srcdoc). */}
          <div
            key="notch"
            className="absolute left-1/2 top-2 z-10 h-4 w-28 -translate-x-1/2 rounded-full bg-[#1a1a1a]"
            style={device === 'mobile' ? undefined : { display: 'none' }}
          />
          <iframe
            key="preview-iframe"
            ref={iframeRef}
            title="preview"
            sandbox="allow-same-origin allow-scripts allow-popups allow-forms"
            className="block border-0 bg-white"
            style={
              device === 'mobile'
                ? { width: 390, height: 844, borderRadius: 28 }
                : { width: '100%', height: '100%' }
            }
          />
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Diffing & dispatch — same logic as before
// ---------------------------------------------------------------------------

type SectionSnap = {
  /** Per-group order — taken from the state's explicit order arrays. */
  orderByGroup: Record<SectionGroup, string[]>;
  sections: Record<string, { group: SectionGroup; config: SectionMetadata }>;
};

function snapshotSections(s: ReturnType<typeof useEditor.getState>): SectionSnap {
  const sections: SectionSnap['sections'] = {};
  for (const [id, cfg] of Object.entries(s.sections)) sections[id] = { group: 'template', config: cfg };
  if (s.headerGroup) {
    for (const [id, cfg] of Object.entries(s.headerGroup.sections))
      sections[id] = { group: 'header-group', config: cfg };
  }
  if (s.footerGroup) {
    for (const [id, cfg] of Object.entries(s.footerGroup.sections))
      sections[id] = { group: 'footer-group', config: cfg };
  }
  if (s.cartDrawerGroup) {
    for (const [id, cfg] of Object.entries(s.cartDrawerGroup.sections))
      sections[id] = { group: 'cart-drawer-group', config: cfg };
  }
  return {
    orderByGroup: {
      template: [...s.order],
      'header-group': [...(s.headerGroup?.order || [])],
      'footer-group': [...(s.footerGroup?.order || [])],
      'cart-drawer-group': [...(s.cartDrawerGroup?.order || [])],
    },
    sections,
  };
}

type Diff =
  | { kind: 'added'; sectionId: string; group: SectionGroup; index: number }
  | { kind: 'removed'; sectionId: string; group: SectionGroup }
  | { kind: 'moved'; sectionId: string; group: SectionGroup; index: number }
  | {
      kind: 'settings';
      sectionId: string;
      group: SectionGroup;
      changed: Array<{ id: string; value: any; kind: 'section' | 'block'; blockId?: string }>;
      structural: boolean;
      disabledChanged: boolean;
      blocksChanged: boolean;
    };

function diffSections(prev: SectionSnap, next: SectionSnap): Diff[] {
  const diffs: Diff[] = [];
  const prevIds = new Set(Object.keys(prev.sections));
  const nextIds = new Set(Object.keys(next.sections));

  for (const id of nextIds) {
    if (!prevIds.has(id)) {
      const ni = next.sections[id];
      const order = orderOf(next, ni.group);
      diffs.push({ kind: 'added', sectionId: id, group: ni.group, index: order.indexOf(id) });
    }
  }
  for (const id of prevIds) {
    if (!nextIds.has(id)) {
      const pi = prev.sections[id];
      diffs.push({ kind: 'removed', sectionId: id, group: pi.group });
    }
  }
  for (const id of nextIds) {
    if (!prevIds.has(id)) continue;
    const pi = prev.sections[id];
    const ni = next.sections[id];
    if (pi.group !== ni.group) continue;
    const prevIdx = orderOf(prev, ni.group).indexOf(id);
    const nextIdx = orderOf(next, ni.group).indexOf(id);
    if (prevIdx !== nextIdx) diffs.push({ kind: 'moved', sectionId: id, group: ni.group, index: nextIdx });
  }
  for (const id of nextIds) {
    if (!prevIds.has(id)) continue;
    const pi = prev.sections[id];
    const ni = next.sections[id];
    if (pi.group !== ni.group) continue;
    const changed = diffSettings(pi.config, ni.config);
    const disabledChanged = pi.config.disabled !== ni.config.disabled;
    const blocksChanged =
      JSON.stringify(pi.config.blockOrder) !== JSON.stringify(ni.config.blockOrder) ||
      Object.keys(pi.config.blocks).length !== Object.keys(ni.config.blocks).length;
    const structural = disabledChanged || blocksChanged;
    if (changed.length > 0 || structural) {
      diffs.push({
        kind: 'settings',
        sectionId: id,
        group: ni.group,
        changed,
        structural,
        disabledChanged,
        blocksChanged,
      });
    }
  }
  return diffs;
}

/** Order (visual, including disabled) of a group from the live store state. */
function orderForGroup(
  state: ReturnType<typeof useEditor.getState>,
  group: SectionGroup
): string[] {
  if (group === 'template') return state.order;
  if (group === 'header-group') return state.headerGroup?.order || [];
  if (group === 'cart-drawer-group') return state.cartDrawerGroup?.order || [];
  return state.footerGroup?.order || [];
}

function orderOf(snap: SectionSnap, group: SectionGroup): string[] {
  return snap.orderByGroup[group] || [];
}

function diffSettings(
  a: SectionMetadata,
  b: SectionMetadata
): Array<{ id: string; value: any; kind: 'section' | 'block'; blockId?: string }> {
  const out: Array<{ id: string; value: any; kind: 'section' | 'block'; blockId?: string }> = [];
  const keys = new Set([...Object.keys(a.settings || {}), ...Object.keys(b.settings || {})]);
  for (const k of keys) {
    if (a.settings?.[k] !== b.settings?.[k]) out.push({ id: k, value: b.settings?.[k], kind: 'section' });
  }
  const blkKeys = new Set([...Object.keys(a.blocks || {}), ...Object.keys(b.blocks || {})]);
  for (const bid of blkKeys) {
    const as = a.blocks?.[bid]?.settings || {};
    const bs = b.blocks?.[bid]?.settings || {};
    const sKeys = new Set([...Object.keys(as), ...Object.keys(bs)]);
    for (const k of sKeys) {
      if (as[k] !== bs[k]) out.push({ id: k, value: bs[k], kind: 'block', blockId: bid });
    }
  }
  return out;
}

/**
 * Per-section timers + sequence counters. A rapid drag on a color picker
 * or range slider would otherwise produce a storm of /render-section
 * requests, each followed by a DOM swap that flashes the section. We
 * coalesce by (1) debouncing per-section, and (2) discarding responses
 * that arrive after a newer request for the same section was scheduled.
 */
const RERENDER_DEBOUNCE_MS = 120;
const rerenderTimers = new Map<string, ReturnType<typeof setTimeout>>();
const rerenderSeq = new Map<string, number>();

/**
 * Full-page reload debounced at 600 ms. Used when a global change
 * might affect parts of the document we don't re-render in-place —
 * the layout, theme.liquid, shared snippets, sticky toggles, etc.
 * Resets on every fresh global change so continuous drags don't
 * tear the iframe down on every tick.
 */
const LAYOUT_RELOAD_DEBOUNCE_MS = 600;
let layoutReloadTimer: ReturnType<typeof setTimeout> | null = null;

function scheduleLayoutReload() {
  if (layoutReloadTimer) clearTimeout(layoutReloadTimer);
  layoutReloadTimer = setTimeout(() => {
    layoutReloadTimer = null;
    const s = useEditor.getState();
    if (!s.session) return;
    // Don't fight an in-flight save — if the user is hitting Salvar at
    // exactly this moment, the subsequent reload will pick up fresh
    // baselines anyway.
    if (s.saveStatus.state === 'saving') return;
    void s.reloadPage();
  }, LAYOUT_RELOAD_DEBOUNCE_MS);
}

function scheduleRerender(
  doc: Document,
  session: NonNullable<ReturnType<typeof useEditor.getState>['session']>,
  sectionId: string,
  group: SectionGroup
) {
  const existing = rerenderTimers.get(sectionId);
  if (existing) clearTimeout(existing);

  const timer = setTimeout(async () => {
    rerenderTimers.delete(sectionId);
    const seq = (rerenderSeq.get(sectionId) || 0) + 1;
    rerenderSeq.set(sectionId, seq);

    // Read the LATEST state at fire time so we always render the freshest
    // settings — not some stale snapshot captured when the timer was set.
    const state = useEditor.getState();
    const found = findSection(state, sectionId);
    if (!found) return;

    try {
      const res = await renderSection(session, {
        sectionId,
        sectionConfig: {
          type: found.section.type,
          settings: found.section.settings,
          blocks: found.section.blocks,
          block_order: found.section.blockOrder,
        },
        // Passa productId/collectionId pra que sections que dependem do
        // produto/coleção atual (main-product, collection-banner, etc.)
        // reproduzam o mesmo estado da página inteira ao invés de cair
        // no fallback "Produto Exemplo 1".
        productId: state.previewProductSlug || undefined,
        collectionId: state.previewCollectionId || undefined,
        globalSettings: state.globalSettings,
        groupName: group,
      });
      // Another newer request came in between start and response — drop this.
      if (rerenderSeq.get(sectionId) !== seq) return;
      replaceSectionHtml(doc, sectionId, res.html);
    } catch (err) {
      if (rerenderSeq.get(sectionId) === seq) {
        console.error('[preview] re-render section failed', err);
      }
    }
  }, RERENDER_DEBOUNCE_MS);

  rerenderTimers.set(sectionId, timer);
}

async function applyDiff(doc: Document, diff: Diff, state: ReturnType<typeof useEditor.getState>) {
  const session = state.session;
  if (!session) return;

  switch (diff.kind) {
    case 'removed':
      removeSectionFromDom(doc, diff.sectionId);
      return;

    case 'moved':
      moveSectionInDom(doc, diff.sectionId, diff.group, diff.index);
      return;

    case 'added': {
      const found = findSection(state, diff.sectionId);
      if (!found) return;
      try {
        const res = await renderSection(session, {
          sectionId: diff.sectionId,
          sectionConfig: {
            type: found.section.type,
            settings: found.section.settings,
            blocks: found.section.blocks,
            block_order: found.section.blockOrder,
          },
          productId: state.previewProductSlug || undefined,
          collectionId: state.previewCollectionId || undefined,
          globalSettings: state.globalSettings,
          groupName: diff.group,
        });
        insertSectionHtml(doc, diff.group, res.html, diff.index);
      } catch (err) {
        console.error('[preview] render added section failed', err);
      }
      return;
    }

    case 'settings': {
      const schemas = state.metadata?.theme.schemas || {};
      const found = findSection(state, diff.sectionId);
      const schema = schemas[found?.section.type || ''];

      // Fast path: ONLY disabled flipped (no setting changes, no block changes).
      // Toggle display via CSS so we skip the server round-trip — no flicker.
      if (
        diff.disabledChanged &&
        !diff.blocksChanged &&
        diff.changed.length === 0 &&
        found
      ) {
        // O engine sanitiza o id (`!` vira `-` etc — ver production.ts
        // `safeSectionKey`), então buscar por `#section-<id>` original
        // não bate pra sections como `product-content!`. Buscamos pelo
        // `data-section-id` que preserva o ID original 1:1 do template.
        const el = doc.querySelector(
          `[data-section-id="${CSS.escape(diff.sectionId)}"]`
        ) as HTMLElement | null;

        if (el) {
          if (found.section.disabled) {
            el.dataset.lcDisabled = 'true';
            el.style.display = 'none';
          } else {
            delete el.dataset.lcDisabled;
            el.style.display = '';
          }
          return;
        }

        // Element not in DOM — the section was disabled on the initial
        // render and got filtered out. Fetch its HTML and insert at the
        // correct index so the user sees it appear. Reveal disabled:
        // this is a toggle back to visible, not a brand-new section,
        // so the user already knows where it is — no auto-scroll.
        if (!found.section.disabled) {
          const order = orderForGroup(state, diff.group);
          const index = order.indexOf(diff.sectionId);
          try {
            const res = await renderSection(session, {
              sectionId: diff.sectionId,
              sectionConfig: {
                type: found.section.type,
                settings: found.section.settings,
                blocks: found.section.blocks,
                block_order: found.section.blockOrder,
              },
              productId: state.previewProductSlug || undefined,
              collectionId: state.previewCollectionId || undefined,
              globalSettings: state.globalSettings,
              groupName: diff.group,
            });
            insertSectionHtml(doc, diff.group, res.html, index, { reveal: false });
          } catch (err) {
            console.error('[preview] show-disabled section failed', err);
          }
          return;
        }
      }

      let needsRerender = diff.structural;

      const sectionType = found?.section.type || '';
      for (const ch of diff.changed) {
        if (ch.kind === 'section') {
          const settingSchema = (schema?.settings || []).find((s) => s.id === ch.id);
          const rich = isRichText(settingSchema?.type);
          const { applied } = patchSettingInDom(doc, sectionSettingRef(diff.sectionId, ch.id), ch.value, { richText: rich });
          /*
            Marker injector pula `<style>`/`<script>`, então settings que
            só viram CSS vars (cor/tamanho do título, PIX, WhatsApp, etc.)
            não têm marker → applied === 0. Antes a gente já caía no
            re-render = flick. Agora primeiro tenta `patchCssVar` em
            `#section-<id>` usando o mapa auto-descoberto pelo
            `discoverSectionCssVarsFromLiquid` no boot.
          */
          if (applied === 0) {
            const bindings = sectionCssVarsFor(sectionType, ch.id);
            if (bindings.length === 0) {
              // Diagnóstico: sem binding = discoverer não pegou. Pode
              // ser que o source liquid no metadata.theme.files seja
              // versão antiga (S3/Redis cache stale do engine), OU a
              // setting muda algo que não é CSS var (toggle de markup,
              // valor inline no HTML, etc.) — nesse caso o re-render
              // de section abaixo cobre.
              console.warn(
                `[live-patch] no CSS var binding for "${sectionType}.${ch.id}" — falling back to section re-render.`
              );
            }
            let patchedAnyVar = false;
            for (const b of bindings) {
              const formatted = formatValueForSectionCssVar(ch.value, b.unit);
              if (formatted === null) continue;
              const sectionPatched = patchCssVar(doc, diff.sectionId, b.var, formatted);
              if (sectionPatched) patchedAnyVar = true;
              /*
                Sections "config-only" (cart-drawer-config) declaram a
                var em `:root { ... }` pra que markup IRMÃO/ANCESTOR
                (snippet drawer-cart) consiga consumir. Sem patchar root
                tb, o `<style>` original mantém o valor antigo no `:root`
                rule e o drawer não muda visualmente até o re-render.

                `header.liquid` é outro caso desse — tem `<head>/<body>`
                aninhados que confundem o HTML parser e o `<header>` final
                acaba fora do `#section-X`. Sem o root patch, mudança de
                cor não afetava o header.
              */
              if (b.rootScoped) {
                const rootPatched = patchCssVarRoot(doc, b.var, formatted);
                if (rootPatched) patchedAnyVar = true;
                console.log(
                  `[live-patch] ${ch.id} → --${b.var}: ${formatted} (section: ${sectionPatched}, root: ${rootPatched})`
                );
              } else {
                console.log(
                  `[live-patch] ${ch.id} → --${b.var}: ${formatted} (section: ${sectionPatched})`
                );
              }
            }
            if (!patchedAnyVar) needsRerender = true;
          }
        } else if (ch.kind === 'block' && ch.blockId) {
          const blkType = findSection(state, diff.sectionId)?.section.blocks[ch.blockId]?.type;
          const bSchema = (schema?.blocks || []).find((b) => b.type === blkType);
          const settingSchema = (bSchema?.settings || []).find((s) => s.id === ch.id);
          const rich = isRichText(settingSchema?.type);
          const { applied } = patchSettingInDom(doc, blockSettingRef(ch.blockId, ch.id), ch.value, { richText: rich });
          if (applied === 0) needsRerender = true;
        }
      }

      if (needsRerender) {
        scheduleRerender(doc, session, diff.sectionId, diff.group);
        /*
          REMOVIDO: chamar `scheduleLayoutReload()` aqui causava LOOP
          infinito.

          Cadeia:
            1. User muda setting → state change
            2. Preview subscriber dispara → applyDiff settings
            3. `needsRerender = true` → scheduleLayoutReload (600ms)
            4. reloadPage → hydrateFromResponse → state change
            5. Preview subscriber dispara DE NOVO → diff (sections do
               hydrate vs prev) → applyDiff → goto 3.

          A cada ciclo o iframe re-renderiza (srcdoc nuke + remount),
          tudo trava num loop visual com console floodado de
          "[skip-cart] ...", "[cart-combo] ...", "[sticky-header] ...".

          Fix: confiar SÓ no `scheduleRerender` (troca HTML da section
          única, sem re-hidratar state). Snippets compartilhados que
          aparecem em outras sections vão ficar com versão antiga
          até o próximo save ou F5 — trade-off aceitável vs travar
          o editor.
        */
      }
      return;
    }
  }
}
